import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "@context/AuthContext";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  limit,
} from "firebase/firestore";
import { db } from "@lib/firebase";
import { jsPDF } from "jspdf";

export const SuratPernyataan = () => {
  const { user } = useAuth();
  const [namaLengkap, setNamaLengkap] = useState("");
  const [teamName, setTeamName] = useState("");
  const [isGroupRegistration, setIsGroupRegistration] = useState(false);
  const [tanggal, setTanggal] = useState("");
  const [isReady, setIsReady] = useState(false);
  const printRef = useRef(null);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) return;

      try {
        // Ambil data pengguna dari koleksi users
        const userDocRef = doc(db, "users", user.uid);
        const userDocSnap = await getDoc(userDocRef);

        if (userDocSnap.exists()) {
          const data = userDocSnap.data();
          setNamaLengkap(
            data.namaLengkap || user.displayName || "Nama Tidak Ditemukan"
          );
        } else {
          setNamaLengkap(user.displayName || "Nama Tidak Ditemukan");
        }

        // Cek apakah pengguna terdaftar dalam kategori grup ULTRA24
        const registrationQuery = query(
          collection(db, "pendaftaranUltra24"),
          where("uid", "==", user.uid),
          where("eventId", "==", "ULTRA24"),
          limit(1)
        );
        const registrationSnapshot = await getDocs(registrationQuery);

        if (!registrationSnapshot.empty) {
          const registrationData = registrationSnapshot.docs[0].data();
          const kategori = registrationData.kategori || "";
          setIsGroupRegistration(kategori.includes("Group"));
          setTeamName(registrationData.teamName || "");
        }
      } catch (err) {
        console.error(
          `❌ Gagal mengambil data pengguna atau pendaftaran untuk user ${user.uid}:`,
          err.message
        );
      }
    };

    fetchUserData();

    const today = new Date();
    const options = { day: "2-digit", month: "long", year: "numeric" };
    setTanggal(today.toLocaleDateString("id-ID", options));
  }, [user]);

  useEffect(() => {
    if (printRef.current && namaLengkap && tanggal) {
      setIsReady(true);
    }
  }, [namaLengkap, tanggal]);

  const generatePDF = async () => {
    if (!isReady || !printRef.current) {
      alert("Dokumen belum siap untuk diunduh. Silakan tunggu sebentar.");
      return;
    }

    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const marginLeft = 20;
    const marginTop = 20;
    const lineHeight = 7;
    const bulletIndent = 5;
    let yPosition = marginTop;

    // Header
    pdf.setFontSize(16);
    pdf.setFont("helvetica", "bold");
    pdf.text("SURAT PERNYATAAN", 105, yPosition, { align: "center" });
    yPosition += lineHeight * 2;

    pdf.setFontSize(12);
    pdf.setFont("helvetica", "normal");

    // Informasi peserta
    pdf.text(`Nama: ${namaLengkap}`, marginLeft, yPosition);
    yPosition += lineHeight;
    if (isGroupRegistration) {
      pdf.text(`Nama Tim: ${teamName || "Unknown"}`, marginLeft, yPosition);
      yPosition += lineHeight;
    }
    pdf.text(`Tanggal: ${tanggal}`, marginLeft, yPosition);
    yPosition += lineHeight * 2;

    // Isi surat
    const paragraphs = [
      `Dengan ini menyatakan bahwa saya secara sukarela dan sadar mendaftarkan diri${
        isGroupRegistration ? " dan tim saya" : ""
      } sebagai peserta dalam kegiatan ULTRA24 yang diselenggarakan oleh Cakrawala Jakarta Kreatif.`,
      "Saya memahami bahwa kegiatan ini bersifat trail run yang menantang secara fisik dan mental, serta memiliki potensi risiko, seperti kondisi cuaca ekstrem, kontur medan yang berat, dan gangguan kesehatan lainnya. Oleh karena itu, saya menyatakan bahwa saya telah mempersiapkan diri secara optimal untuk mengikuti lomba ini dan dalam kondisi kesehatan yang baik.",
      `Saya juga menyadari bahwa panitia penyelenggara, relawan, sponsor, maupun pihak-pihak terkait lainnya tidak bertanggung jawab secara hukum atas risiko cedera, kecelakaan, kehilangan, atau kejadian darurat lainnya yang mungkin ${
        isGroupRegistration ? "saya atau anggota tim saya" : "saya"
      } alami selama mengikuti rangkaian kegiatan. Seluruh tanggung jawab atas keselamatan dan kesehatan pribadi selama kegiatan merupakan tanggung jawab ${
        isGroupRegistration ? "saya dan anggota tim saya" : "saya sendiri"
      }, dan saya melepaskan panitia dari segala bentuk tuntutan hukum atas kejadian yang mungkin timbul selama atau akibat keikutsertaan ${
        isGroupRegistration ? "saya dan tim saya" : "saya"
      } dalam kegiatan ini.`,
      "Saya berkomitmen untuk:",
      [
        "Mengikuti seluruh peraturan dan arahan dari panitia dan pihak berwenang.",
        "Menjaga sportivitas, keamanan, dan ketertiban selama kegiatan berlangsung.",
        "Tidak melakukan tindakan yang merugikan diri sendiri, peserta lain, panitia, maupun lingkungan.",
      ],
      `Saya juga memberikan izin penuh kepada panitia untuk menggunakan dokumentasi (foto, video, atau lainnya) yang memuat identitas atau citra ${
        isGroupRegistration ? "saya dan anggota tim saya" : "saya"
      } untuk keperluan publikasi, promosi, dan dokumentasi kegiatan tanpa memerlukan izin tambahan.`,
      `Dengan ini, saya menyatakan telah membaca, memahami, dan menyetujui seluruh isi pernyataan ini dengan sadar, sukarela, dan tanpa tekanan dari pihak mana pun. Pernyataan ini saya buat sebagai bentuk komitmen dan tanggung jawab ${
        isGroupRegistration ? "saya dan tim saya" : "saya"
      } sebagai peserta dalam ULTRA24.`,
    ];

    paragraphs.forEach((item) => {
      if (Array.isArray(item)) {
        item.forEach((bullet) => {
          // Pecah teks bullet ke baris-baris agar tidak terpotong
          const lines = pdf.splitTextToSize(bullet, 170 - bulletIndent);
          lines.forEach((line, index) => {
            if (yPosition + lineHeight > 277) {
              pdf.addPage();
              yPosition = marginTop;
            }
            const text = index === 0 ? `• ${line}` : line;
            const xOffset =
              index === 0 ? marginLeft : marginLeft + bulletIndent;
            pdf.text(text, xOffset, yPosition);
            yPosition += lineHeight;
          });
        });
      } else {
        const lines = pdf.splitTextToSize(item, 170);
        lines.forEach((line) => {
          if (yPosition + lineHeight > 277) {
            pdf.addPage();
            yPosition = marginTop;
          }
          pdf.text(line, marginLeft, yPosition);
          yPosition += lineHeight;
        });
      }
      yPosition += lineHeight;
    });

    // Tanda tangan
    if (yPosition + lineHeight * 3 > 277) {
      pdf.addPage();
      yPosition = marginTop;
    }
    pdf.text("Hormat saya,", 170, yPosition, { align: "right" });
    yPosition += lineHeight * 2;
    pdf.setFont("helvetica", "bold");
    pdf.text(namaLengkap, 170, yPosition, { align: "right" });

    // Nomor halaman
    const pageCount = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      pdf.setPage(i);
      pdf.setFontSize(10);
      pdf.text(`Halaman ${i} dari ${pageCount}`, 105, 287, { align: "center" });
    }

    const fileName = `Surat_Pernyataan_${namaLengkap.replace(/\s+/g, "_")}${
      isGroupRegistration ? `_${teamName.replace(/\s+/g, "_")}` : ""
    }.pdf`;
    pdf.save(fileName);
    console.log(`✅ PDF diunduh: ${fileName}`);
  };

  if (!namaLengkap || !tanggal) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="text-center text-gray-600 text-lg font-semibold p-6">
          Memuat data...
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 py-20 px-4">
      <div
        ref={printRef}
        className="bg-white p-4 sm:p-8 shadow-lg rounded-xl text-justify border border-gray-200 w-full max-w-[21cm] mx-auto"
      >
        <h2 className="text-xl sm:text-2xl font-bold text-center mb-6 text-gray-800">
          SURAT PERNYATAAN
        </h2>

        <p className="mb-4 text-gray-700 text-sm sm:text-base">
          <strong className="text-gray-900">Nama:</strong> {namaLengkap}
        </p>
        {isGroupRegistration && (
          <p className="mb-4 text-gray-700 text-sm sm:text-base">
            <strong className="text-gray-900">Nama Tim:</strong>{" "}
            {teamName || "Unknown"}
          </p>
        )}
        <p className="mb-6 text-gray-700 text-sm sm:text-base">
          <strong className="text-gray-900">Tanggal:</strong> {tanggal}
        </p>

        <p className="mb-4 text-gray-700 leading-relaxed text-sm sm:text-base">
          Dengan ini menyatakan bahwa saya secara sukarela dan sadar
          mendaftarkan diri{isGroupRegistration ? " dan tim saya" : ""} sebagai
          peserta dalam kegiatan Jakarta Open TrailRun (JOTR) 2025 - KEJURDA I
          yang diselenggarakan oleh ALTI DKI Jakarta, yang akan dilaksanakan
          pada tanggal 27 - 28 September 2025 di kawasan Taman Nasional Gunung
          Gede Pangrango.
        </p>

        <p className="mb-4 text-gray-700 leading-relaxed text-sm sm:text-base">
          Saya memahami bahwa kegiatan ini bersifat trail run yang menantang
          secara fisik dan mental, serta memiliki potensi risiko, seperti
          kondisi cuaca ekstrem, kontur medan yang berat, dan gangguan kesehatan
          lainnya. Oleh karena itu, saya menyatakan bahwa saya telah
          mempersiapkan diri secara optimal untuk mengikuti lomba ini dan dalam
          kondisi kesehatan yang baik.
        </p>

        <p className="mb-4 text-gray-700 leading-relaxed text-sm sm:text-base">
          Saya juga menyadari bahwa panitia penyelenggara, relawan, sponsor,
          maupun pihak-pihak terkait lainnya tidak bertanggung jawab secara
          hukum atas risiko cedera, kecelakaan, kehilangan, atau kejadian
          darurat lainnya yang mungkin{" "}
          {isGroupRegistration ? "saya atau anggota tim saya" : "saya"} alami
          selama mengikuti rangkaian kegiatan. Seluruh tanggung jawab atas
          keselamatan dan kesehatan pribadi selama kegiatan merupakan tanggung
          jawab{" "}
          {isGroupRegistration ? "saya dan anggota tim saya" : "saya sendiri"},
          dan saya melepaskan panitia dari segala bentuk tuntutan hukum atas
          kejadian yang mungkin timbul selama atau akibat keikutsertaan{" "}
          {isGroupRegistration ? "saya dan tim saya" : "saya"} dalam kegiatan
          ini.
        </p>

        <p className="mb-4 text-gray-700 leading-relaxed text-sm sm:text-base">
          Saya berkomitmen untuk:
        </p>
        <ul className="mb-4 text-gray-700 leading-relaxed text-sm sm:text-base list-disc list-inside">
          <li>
            Mengikuti seluruh peraturan dan arahan dari panitia dan pihak
            berwenang.
          </li>
          <li>
            Menjaga sportivitas, keamanan, dan ketertiban selama kegiatan
            berlangsung.
          </li>
          <li>
            Tidak melakukan tindakan yang merugikan diri sendiri, peserta lain,
            panitia, maupun lingkungan.
          </li>
        </ul>

        <p className="mb-4 text-gray-700 leading-relaxed text-sm sm:text-base">
          Saya juga memberikan izin penuh kepada panitia untuk menggunakan
          dokumentasi (foto, video, atau lainnya) yang memuat identitas atau
          citra {isGroupRegistration ? "saya dan anggota tim saya" : "saya"}{" "}
          untuk keperluan publikasi, promosi, dan dokumentasi kegiatan tanpa
          memerlukan izin tambahan.
        </p>

        <p className="mb-4 text-gray-700 leading-relaxed text-sm sm:text-base">
          Dengan ini, saya menyatakan telah membaca, memahami, dan menyetujui
          seluruh isi pernyataan ini dengan sadar, sukarela, dan tanpa tekanan
          dari pihak mana pun. Pernyataan ini saya buat sebagai bentuk komitmen
          dan tanggung jawab{" "}
          {isGroupRegistration ? "saya dan tim saya" : "saya"} sebagai peserta
          dalam JOTR 2025.
        </p>

        <p className="mt-6 text-left text-gray-700 text-sm sm:text-base">
          Hormat saya,
        </p>
        <p className="mt-4 text-left font-bold text-gray-900 text-sm sm:text-base">
          {namaLengkap}
        </p>
      </div>

      <button
        onClick={generatePDF}
        disabled={!isReady}
        className={`mt-6 px-6 py-2 rounded-xl font-semibold text-white transition-colors duration-200 ${
          isReady
            ? "bg-red-600 hover:bg-red-700"
            : "bg-gray-400 cursor-not-allowed"
        }`}
      >
        Unduh PDF
      </button>
    </div>
  );
};
